import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { ChevronLeft, Tag, PlusCircle, MinusCircle } from "lucide-react";
import {
  addToCart,
  removeFromCart,
  updateCartQuantity,
} from "@/store/slices/cartSlice";
import apis from "@/utils/apis";
import { formatPrice, getDiscount, calculatePricePerUnit, cn, isLiterBasedUnit, isKGVariant, hasLiquidVariants, formatWeightUnitShort } from "@/utils/utils";
import useApi from "@/utils/useApi";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import ImageSlider from "@/components/ImageSlider";
import { SUPPORT_IMAGE_URL } from "@/utils/constants";
import ProductDescription from "@/components/ProductDescription";
import ProductCard from "@/components/ProductCard";
import useAppConfig from "@/hooks/useAppConfig";

const ProductDetailsPage = () => {
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const productIdFromQuery = searchParams.get('productId');
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if user came from product list
  const fromProductList = location.state?.fromProductList;
  const dispatch = useDispatch();
  const cartProducts = useSelector((state) => state?.cart?.cartProducts);
  const { callApi: getProductDetails, data: productData, isLoading } = useApi();
  const { t } = useTranslation();
  const user = useSelector((state) => state.user.userInfo);
  const martId = user?.defaultAddress?.nearby?.mart._id;
  const { defaultReturnPolicy } = useAppConfig();

  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(0);
  const [loadingVariant, setLoadingVariant] = useState(null);
  const [initialLoad, setInitialLoad] = useState(true);

  const cartProduct = cartProducts?.find(
    (item) => item?.stockId === selectedVariant?._id
  );
  const isCartQuantityMoreThanAvailable =
    cartProduct?.quantity > productData?.availableQuantity;
  const quantityInputRef = useRef(null);

  // Check if product is paused
  const isProductPaused = () => {
    if (!selectedVariant?.pausedOnApp) return false;
    
    // If pausedOnApp is true but no pauseAfter field, product is paused
    if (!selectedVariant?.pauseAfter) return true;
    
    // If pauseAfter exists, check if current time is past the pause time
    const pauseTime = new Date(selectedVariant.pauseAfter);
    const currentTime = new Date();
    return currentTime > pauseTime;
  };

  const productPaused = isProductPaused();

  useEffect(() => {
    // If productIdFromQuery exists, use the product by ID API (for out of stock products)
    if (productIdFromQuery) {
      setLoadingVariant(productId);
      getProductDetails({
        api: apis.getProductById,
        param: productIdFromQuery,
      }).then((response) => {
        if (response) {
          // For getProductById API, the response structure is different
          // variants array contains variant info directly, no nested productId
          const firstVariant = response.variants?.[0];
          
          const transformedResponse = {
            _id: productId, // stockId from URL
            productId: {
              _id: response._id,
              name: response.name,
              description: response.description,
              images: response.images,
              variant: firstVariant ? {
                title: firstVariant.title,
                weight: firstVariant.weight,
                weightUnit: firstVariant.weightUnit,
              } : null,
              variants: response.variants, // Store all variants for variant selector
            },
            price: 0,
            sellingPrice: 0,
            availableQuantity: 0, // Out of stock
            pausedOnApp: false,
            relatedStock: [], // No related stock for out of stock products
          };
          setSelectedVariant(transformedResponse);
        }
      }).finally(() => {
        setLoadingVariant(null);
        setInitialLoad(false);
      });
    } else if (martId && productId && productId !== "undefined") {
      // Normal flow - use stock details API
      setLoadingVariant(productId);
      getProductDetails({
        api: apis.getProductDetails,
        param: `${martId}/stock/${productId}`,
      }).then((response) => {
        if (response) {
          // Set initial selected variant
          const initialVariant = response._id === productId
            ? response
            : response?.relatedStock?.find((stock) => stock._id === productId);
          setSelectedVariant(initialVariant || response);
        }
      }).finally(() => {
        setLoadingVariant(null);
        setInitialLoad(false);
      });
    }
  }, [productId, martId, productIdFromQuery, getProductDetails]);

  useEffect(() => {
    if(quantityInputRef.current){
      setTimeout(() => {
        quantityInputRef.current.value = quantity;
      }, 0);
    }
  }, [quantity])

  useEffect(() => {
    if (selectedVariant) {
      if (cartProduct) {
        setQuantity(cartProduct?.quantity);
      } else {
        setQuantity(1);
      }
    }
  }, [selectedVariant, cartProduct]);

  const handleAddToCart = () => {
    if (productPaused) {
      toast.error(t("productDetails.productNotAvailable"));
      return;
    }
    
    if (!cartProduct) {
      dispatch(
        addToCart({
          _id: selectedVariant?.productId?._id,
          price: selectedVariant?.price,
          sellingPrice: selectedVariant?.sellingPrice,
          name: selectedVariant?.productId?.name,
          images: selectedVariant?.productId?.images,
          variant: selectedVariant?.productId?.variant,
          stockId: selectedVariant?._id,
          quantity: quantity || 1,
          availableQuantity: selectedVariant?.availableQuantity
        })
      );
    }
  };

  const handleQuantityChange = (change) => {
    let newQuantity = change;
    if (newQuantity >= 0 || newQuantity === "") {
      toast.dismiss();
      if (newQuantity > productData?.availableQuantity) {
        toast.error(
          `Only ${productData?.availableQuantity} items available in stock.`
        );
        newQuantity = productData?.availableQuantity || 0;
      }
      setQuantity(newQuantity);
      if (!newQuantity && cartProduct) {
        return dispatch(removeFromCart(productId));
      }
      if (cartProduct) {
        dispatch(
          updateCartQuantity({
            productId,
            quantity: newQuantity || 0,
            variantStockId: selectedVariant?._id,
          })
        );
      }
    }
  };

  const handleBuyNow = () => {
    if (productPaused) {
      toast.error(t("productDetails.productNotAvailable"));
      return;
    }
    
    if (!cartProduct) {
      handleAddToCart();
    }
    navigate("/cart");
  };

  const handleVariantSelect = (variant, variantStockId = null) => {
    // For out of stock products with getProductById API response
    // variant is from productId.variants array and we need to construct a stockId
    const targetStockId = variantStockId || variant._id;
    
    if (targetStockId !== selectedVariant?._id) {
      window.history.replaceState({}, '', `/product/${targetStockId}`);
      setLoadingVariant(targetStockId);
      
      // First try to get stock details
      getProductDetails({
        api: apis.getProductDetails,
        param: `${martId}/stock/${targetStockId}`,
      }).then((response) => {
        // Check if response has actual stock data or just empty relatedStock
        if (response && response.relatedStock !== undefined && response.relatedStock.length === 0) {
          // Out of stock response: { relatedStock: [] }
          // Just update the variant selection, keep other data as is
          if (variant && selectedVariant?.productId) {
            const updatedVariant = {
              ...selectedVariant,
              _id: targetStockId,
              productId: {
                ...selectedVariant.productId,
                variant: {
                  title: variant.title,
                  weight: variant.weight,
                  weightUnit: variant.weightUnit,
                }
              },
              price: 0,
              sellingPrice: 0,
              availableQuantity: 0,
            };
            setSelectedVariant(updatedVariant);
          }
        } else if (response) {
          // Stock found - product is available
          const newVariant = response._id === targetStockId
            ? response
            : response?.relatedStock?.find((stock) => stock._id === targetStockId);
          setSelectedVariant(newVariant || response);
        }
      }).catch(() => {
        // API error - product is out of stock
        // Update selected variant with the clicked variant data
        if (variant && selectedVariant?.productId) {
          const updatedVariant = {
            ...selectedVariant,
            _id: targetStockId,
            productId: {
              ...selectedVariant.productId,
              variant: {
                title: variant.title,
                weight: variant.weight,
                weightUnit: variant.weightUnit,
              }
            },
            price: 0,
            sellingPrice: 0,
            availableQuantity: 0,
          };
          setSelectedVariant(updatedVariant);
        }
      }).finally(() => {
        setLoadingVariant(null);
      });
    }
  };

  if (initialLoad && isLoading) {
    return (
      <div className="font-sans flex items-center justify-center grow">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-100"></div>
      </div>
    );
  }

  if (!selectedVariant) {
    return (
      <div className="font-sans flex items-center justify-center grow">
        <p className="text-xl text-gray-600">{t("productDetails.notFound")}</p>
      </div>
    );
  }

  const discount = getDiscount(
    selectedVariant?.price,
    selectedVariant?.sellingPrice
  );

  return (
    <div className="font-sans">
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
          <button 
            onClick={() => navigate(-1)} 
            className="text-gray-600 transition-colors"
          >
            <ChevronLeft className="w-6 h-6 relative mb-[2px]" />
          </button>
          <h1 className="text-xl font-bold">{t("productDetails.title")}</h1>
        </div>
      </header>
      <main className="bg-white dark:bg-transparent">
        <motion.div className="rounded-lg shadow-md overflow-hidden">
          <div className="relative">
            <ImageSlider
              images={[...(selectedVariant?.productId?.images || []), SUPPORT_IMAGE_URL]}
              alt={selectedVariant?.productId?.name}
              className="h-64"
              showNavigation={true}
              showDots={true}
              autoSlide={false}
              disabled={productPaused}
            />
          </div>
          <div className="p-3">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {selectedVariant?.productId?.name}
            </h2>
            {selectedVariant?.sellingPrice > 0 && (
              <div className="flex items-center justify-between mb-4">
                <div>
                  <span className="text-3xl font-bold text-primary-100">
                    ₹{formatPrice(selectedVariant?.sellingPrice)}
                  </span>
                  {selectedVariant?.price !== selectedVariant?.sellingPrice && (
                    <span className="text-lg text-gray-500 line-through ml-2">
                      ₹{formatPrice(selectedVariant?.price)}
                    </span>
                  )}
                  {/* Price per unit display */}
                  {selectedVariant?.productId?.variant?.weight && 
                   selectedVariant?.productId?.variant?.weightUnit && 
                   selectedVariant?.sellingPrice && (
                    <div className="text-sm text-gray-500 mt-1">
                      {calculatePricePerUnit(
                        selectedVariant.sellingPrice, 
                        selectedVariant.productId.variant.weight, 
                        selectedVariant.productId.variant.weightUnit
                      )}
                    </div>
                  )}
                </div>
                {selectedVariant?.price !== selectedVariant?.sellingPrice && (
                  <div className="bg-green-100 text-green-800 text-sm font-medium px-2.5 py-0.5 rounded-full flex items-center">
                    <Tag className="w-4 h-4 mr-1" />
                    {discount}% {t("productDetails.off")}
                  </div>
                )}
              </div>
            )}
            <div className="mb-6">
              <label
                htmlFor="variant"
                className="text-sm font-medium text-gray-700 mb-1 block"
              >
                {t("productDetails.selectVariant")}
              </label>

              <div className="grid grid-cols-2 gap-2 mt-2">
                {/* For out of stock products (productIdFromQuery), show variants from productId.variants array */}
                {productIdFromQuery && selectedVariant?.productId?.variants
                  ?.filter((variant) => {
                    // If product has liquid variants, hide KG variants
                    if (hasLiquidVariants(selectedVariant?.productId?.variants)) {
                      return !isKGVariant(variant.title);
                    }
                    return true;
                  })
                  ?.map((variant, index) => {
                  // Use the variant's actual _id from the variants array
                  const variantStockId = variant._id;
                  const isSelected = selectedVariant?.productId?.variant?.title === variant.title;
                  
                  return (
                    <button
                      key={variant._id || index}
                      onClick={() => handleVariantSelect(variant, variantStockId)}
                      disabled={loadingVariant === variantStockId}
                      className={`border ${
                        isSelected
                          ? "border-primary-100 bg-primary-100/10"
                          : "border-gray-300"
                      } rounded-md p-3 text-sm flex flex-col items-center justify-center transition-colors hover:border-primary-100 disabled:opacity-50 disabled:cursor-not-allowed relative`}
                    >
                      {loadingVariant === variantStockId && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-gray-800/50 rounded-md">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-100"></div>
                        </div>
                      )}
                      <span className="font-medium">
                        {variant.title}
                      </span>
                      {variant.title && !isLiterBasedUnit(variant.title) && (
                        <span className="text-xs text-gray-500 mt-1">
                          {variant.weight} {formatWeightUnitShort(variant.weightUnit)}
                        </span>
                      )}
                    </button>
                  );
                })}

                {/* For in-stock products (normal flow), show main product and related stock */}
                {!productIdFromQuery && productData && (() => {
                  const allVariants = [productData, ...(productData?.relatedStock || [])];
                  const shouldHideKG = hasLiquidVariants(allVariants) && isKGVariant(productData.productId?.variant?.title);
                  
                  // Don't render if it's a KG variant in a liquid product
                  if (shouldHideKG) return null;
                  
                  return (
                    <button
                      key={productData._id}
                      onClick={() => handleVariantSelect(productData)}
                      disabled={loadingVariant === productData._id}
                      className={`border ${
                        productData._id === selectedVariant?._id
                          ? "border-primary-100 bg-primary-100/10"
                          : "border-gray-300"
                      } rounded-md p-3 text-sm flex flex-col items-center justify-center transition-colors hover:border-primary-100 disabled:opacity-50 disabled:cursor-not-allowed relative`}
                    >
                      {loadingVariant === productData._id && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-gray-800/50 rounded-md">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-100"></div>
                        </div>
                      )}
                      <span className="font-medium">
                        {productData.productId?.variant?.title}
                      </span>
                      {productData.productId?.variant?.title && !isLiterBasedUnit(productData.productId?.variant?.title) && (
                        <span className="text-xs text-gray-500 mt-1">
                          {productData.productId?.variant?.weight}{" "}
                          {productData.productId?.variant?.weightUnit}
                        </span>
                      )}
                    </button>
                  );
                })()}

                {/* Map through related stock variants */}
                {!productIdFromQuery && productData?.relatedStock
                  ?.filter((variant) => {
                    // Filter out current product
                    if (variant._id === productData._id) return false;
                    // If product has liquid variants, hide KG variants
                    const allVariants = [productData, ...(productData?.relatedStock || [])];
                    if (hasLiquidVariants(allVariants)) {
                      return !isKGVariant(variant.productId?.variant?.title);
                    }
                    return true;
                  })
                  .map((variant) => {
                    const variantData = variant.productId?.variant;
                    // Check if this is the currently selected variant
                    const isCurrentVariant = variant._id === selectedVariant?._id;

                    return (
                      <button
                        key={variant._id}
                        onClick={() => handleVariantSelect(variant)}
                        disabled={loadingVariant === variant._id}
                        className={`border ${
                          isCurrentVariant
                            ? "border-primary-100 bg-primary-100/10"
                            : "border-gray-300"
                        } rounded-md p-3 text-sm flex flex-col items-center justify-center transition-colors hover:border-primary-100 disabled:opacity-50 disabled:cursor-not-allowed relative`}
                      >
                        {loadingVariant === variant._id && (
                          <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-gray-800/50 rounded-md">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-100"></div>
                          </div>
                        )}
                        <span className="font-medium">
                          {variantData?.title}
                        </span>
                        {variantData?.title && !isLiterBasedUnit(variantData?.title) && (
                          <span className="text-xs text-gray-500 mt-1">
                            {variantData?.weight} {formatWeightUnitShort(variantData?.weightUnit)}
                          </span>
                        )}
                      </button>
                    );
                  })}
              </div>
            </div>

            {selectedVariant?.availableQuantity > 0 && (
              <div className="flex items-center justify-between mb-6">
                <div
                  className={`flex items-center border border-gray-300 rounded-full ${
                    isCartQuantityMoreThanAvailable && "border-red-500"
                  }`}
                >
                  <button
                    onClick={() => handleQuantityChange(quantity - 1)}
                    className="p-2 text-gray-600 hover:text-primary-100 transition-colors"
                  >
                    <MinusCircle className="w-6 h-6" />
                  </button>
                  <input
                    ref={quantityInputRef}
                    type="number"
                    value={quantity}
                    onChange={(e) => {
                      const newQuantity = parseInt(e.target.value) || 0;
                      handleQuantityChange(newQuantity);
                    }}
                    className="w-16 text-center font-semibold border-none focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none bg-transparent"
                    min="0"
                  />
                  <button
                    onClick={() => handleQuantityChange(quantity + 1)}
                    className="p-2 text-gray-600 hover:text-primary-100 transition-colors"
                  >
                    <PlusCircle className="w-6 h-6" />
                  </button>
                </div>
                {!cartProduct && (
                  <button
                    onClick={handleAddToCart}
                    className={`px-6 py-2 rounded-full font-semibold transition-colors ${
                      productPaused 
                        ? "bg-gray-400 text-white cursor-not-allowed" 
                        : "bg-primary-100 text-white hover:bg-primary-100"
                    }`}
                    disabled={cartProduct}
                  >
                    {t("productDetails.addToCart")}
                  </button>
                )}
              </div>
            )}
            
            {/* Available Stock Info */}
            {selectedVariant?.availableQuantity !== undefined && selectedVariant?.availableQuantity > 0 && (
              <div className="mt-2 mb-4">
                <div className="flex items-center gap-2">
                  <span className={cn(
                    "text-sm font-semibold",
                    (selectedVariant?.availableQuantity - quantity) === 0
                      ? "text-red-600"
                      : "text-green-600"
                  )}>
                    {t("productDetails.unitsRemaining", { count: selectedVariant?.availableQuantity - quantity })}
                  </span>
                </div>
              </div>
            )}

            {selectedVariant?.availableQuantity > 0 ? (
              <button
                onClick={handleBuyNow}
                className={`w-full py-3 rounded-full font-semibold transition-colors ${
                  productPaused 
                    ? "bg-gray-400 text-white cursor-not-allowed" 
                    : "bg-green-500 text-white hover:bg-green-600"
                }`}
              >
                {t("productDetails.buyNow")}
              </button>
            ) : (
              <div className="w-full py-3 rounded-full font-semibold bg-red-500 text-white text-center">
                Out of Stock
              </div>
            )}

            {/* Product Description */}
            {selectedVariant?.productId?.description && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-bold text-gray-800 mb-3">
                  {t("productDetails.productDetailsHeading")}
                </h3>
                <ProductDescription
                  description={selectedVariant?.productId?.description}
                />
              </div>
            )}

            {/* Additional Product Information */}
            {(selectedVariant?.productId?.ingredients?.length > 0 ||
              selectedVariant?.productId?.shelfLife ||
              selectedVariant?.productId?.manufacturerName ||
              selectedVariant?.productId?.manufacturerAddress ||
              selectedVariant?.productId?.seller?.name ||
              selectedVariant?.productId?.seller?.address ||
              selectedVariant?.productId?.fssaiLicense ||
              selectedVariant?.productId?.sellerFssai ||
              selectedVariant?.productId?.disclaimer ||
              selectedVariant?.productId?.returnPolicy ||
              defaultReturnPolicy) && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-bold text-gray-800 mb-3">
                  {t("productDetails.productInformationHeading")}
                </h3>
                <div className="space-y-4">
                  {/* Ingredients */}
                  {selectedVariant?.productId?.ingredients?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-1">
                        {t("productDetails.ingredients")}
                      </h4>
                      <ul className="text-sm text-gray-600 list-disc list-inside">
                        {selectedVariant.productId.ingredients.map((ingredient, index) => (
                          <li key={index}>{ingredient}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Shelf Life */}
                  {selectedVariant?.productId?.shelfLife && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-1">
                        {t("productDetails.shelfLife")}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {selectedVariant.productId.shelfLife}
                      </p>
                    </div>
                  )}

                  {/* Manufacturer Name */}
                  {selectedVariant?.productId?.manufacturerName && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-1">
                        {t("productDetails.manufacturerName")}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {selectedVariant.productId.manufacturerName}
                      </p>
                    </div>
                  )}

                  {/* Manufacturer Address */}
                  {selectedVariant?.productId?.manufacturerAddress && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-1">
                        {t("productDetails.manufacturerAddress")}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {selectedVariant.productId.manufacturerAddress}
                      </p>
                    </div>
                  )}

                  {/* Seller Name */}
                  {selectedVariant?.productId?.seller?.name && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-1">
                        {t("productDetails.seller")}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {selectedVariant.productId.seller.name}
                      </p>
                    </div>
                  )}

                  {/* Seller Address */}
                  {selectedVariant?.productId?.seller?.address && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-1">
                        {t("productDetails.sellerAddress")}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {selectedVariant.productId.seller.address}
                      </p>
                    </div>
                  )}

                  {/* FSSAI License */}
                  {selectedVariant?.productId?.fssaiLicense && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-1">
                        {t("productDetails.fssaiLicense")}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {selectedVariant.productId.fssaiLicense}
                      </p>
                    </div>
                  )}

                  {/* Seller FSSAI */}
                  {selectedVariant?.productId?.sellerFssai && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-1">
                        {t("productDetails.sellerFssai")}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {selectedVariant.productId.sellerFssai}
                      </p>
                    </div>
                  )}

                  {/* Disclaimer */}
                  {selectedVariant?.productId?.disclaimer && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-1">
                        {t("productDetails.disclaimer")}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {selectedVariant.productId.disclaimer}
                      </p>
                    </div>
                  )}

                  {/* Return Policy - Use product's returnPolicy or default from config */}
                  {(selectedVariant?.productId?.returnPolicy || defaultReturnPolicy) && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-1">
                        {t("productDetails.returnPolicy")}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {selectedVariant?.productId?.returnPolicy || defaultReturnPolicy}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Related Products */}
            {!productIdFromQuery && productData?.relatedStock && productData.relatedStock.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-bold text-gray-800 mb-4">
                  Related Products
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {productData.relatedStock
                    .filter((stock) => stock._id !== selectedVariant?._id)
                    .map((stock) => {
                      // Transform relatedStock data to match ProductCard expected format
                      const cardData = {
                        _id: stock.productId?._id,
                        name: stock.productId?.name,
                        description: stock.productId?.description,
                        images: stock.productId?.images,
                        variants: [stock.productId?.variant],
                        stockEntries: [
                          {
                            _id: stock._id,
                            price: stock.price,
                            sellingPrice: stock.sellingPrice,
                            availableQuantity: stock.availableQuantity,
                            pausedOnApp: stock.pausedOnApp,
                            pauseAfter: stock.pauseAfter,
                          },
                        ],
                      };
                      
                      return (
                        <ProductCard
                          key={stock._id}
                          data={cardData}
                          loading={false}
                        />
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default ProductDetailsPage;
